import { useEffect, useState, type FormEvent } from 'react';
import { CalendarDays, Sun, Sunrise } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { HOLIDAY_DAY_NOTE_LEGEND, holidayDayNote } from '@/features/institute/holidays/utils';
import { examsApi } from '@/features/teacher/api/exams.api';
import {
  EXAM_BALANCE_PLAN_OPTIONS,
  EXAM_SESSION_LABEL,
  instituteExamTypeOptions,
  earliestExamDate,
} from '@/features/teacher/exams/constants';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { capitalizeWords } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { Exam, ExamBalancePlan, ExamPublication, ExamType } from '@/types/exam';
import type { Holiday } from '@/types/holiday';
import type { AllottableSubject } from '@/types/timetableAdmin';
import { formatClockTime } from '../timetableWeek';

/**
 * Setting a paper, and correcting one.
 *
 * One component for both, because they are the same eight fields — they were
 * two files of three hundred near-identical lines, and the edit form had
 * already drifted: its subject picker was never sent anywhere, so changing it
 * did nothing at all.
 *
 * **Every control here is stored.** The session, the timings and what the rest
 * of the day holds used to be collected and dropped on the floor, and the exam
 * table then invented a session from the row's position in the list. They are
 * columns on `exams` now.
 *
 * **The session is derived, never chosen.** Which half of the day a paper sits
 * in is decided by when it starts, by the server, in one place — so the three
 * buttons below fill the timings rather than setting a session of their own. A
 * paper moved to two in the afternoon becomes an afternoon paper because its
 * time says so, not because somebody remembered to press a second control.
 */

/** What an Indian institute's exam day usually looks like. */
const TIMING_PRESETS = [
  { value: 'MORNING', label: 'Morning', startTime: '09:30', endTime: '12:30', icon: Sunrise },
  { value: 'AFTERNOON', label: 'Afternoon', startTime: '13:30', endTime: '16:30', icon: Sun },
  { value: 'FULL_DAY', label: 'Full day', startTime: '09:30', endTime: '16:00', icon: CalendarDays },
] as const;

export interface ExamPaperModalProps {
  /** The section the paper is for. Fixed — a paper's section is never editable. */
  sectionUuid: string;
  sectionName: string;
  /** Subjects allotted in this section. Only needed when setting a new paper. */
  subjects: AllottableSubject[];
  /** The paper being corrected, or null when setting a new one. */
  exam: Exam | null;
  /** Where the date box starts on a new paper — the week on screen. */
  defaultDate: string;
  /**
   * The Academic Calendar's holidays, marked on the date picker so a paper is
   * not set on a day the school is closed by accident. Null while they load.
   */
  holidays: Holiday[] | null;
  onClose: () => void;
  onSaved: () => void;
}

export function ExamPaperModal({
  sectionUuid,
  sectionName,
  subjects,
  exam,
  defaultDate,
  holidays,
  onClose,
  onSaved,
}: ExamPaperModalProps) {
  const isEditing = exam !== null;

  const [subjectUuid, setSubjectUuid] = useState(exam?.subject.uuid ?? subjects[0]?.uuid ?? '');

  useEffect(() => {
    if (!isEditing && !subjectUuid && subjects.length > 0) {
      setSubjectUuid(subjects[0].uuid);
    }
  }, [isEditing, subjectUuid, subjects]);
  const [name, setName] = useState(exam?.name ?? '');
  const [examType, setExamType] = useState<ExamType>(exam?.examType ?? 'FORMATIVE');
  const [examDate, setExamDate] = useState(exam?.examDate ?? defaultDate);
  const [startTime, setStartTime] = useState(exam?.startTime ?? '09:30');
  const [endTime, setEndTime] = useState(exam?.endTime ?? '12:30');
  const [balancePlan, setBalancePlan] = useState<ExamBalancePlan>(
    exam?.balancePlan ?? 'PREPARATION_LEAVE',
  );
  const [maxMarks, setMaxMarks] = useState(String(exam?.maxMarks ?? 100));
  const [passMarks, setPassMarks] = useState(
    exam && !exam.passMarksFromInstituteDefault ? String(exam.passMarks) : '',
  );
  /** Which of the save buttons is working, so only that one spins. */
  const [savingAs, setSavingAs] = useState<ExamPublication | null>(null);
  const isSaving = savingAs !== null;
  /** A new paper, or a draft, can still be kept back from the class. */
  const canSaveDraft = !isEditing || exam.publication === 'DRAFT';

  // Read off the start time, exactly as the server reads it — so the badge
  // cannot say "morning" about a paper the timetable will print as afternoon.
  const session = startTime < '12:00' ? 'MORNING' : 'AFTERNOON';
  const endsBeforeItStarts = endTime <= startTime;
  const passAboveTotal =
    passMarks.trim() !== '' && Number(passMarks) > Number(maxMarks || 0);
  const hasSubjects = subjects.length > 0;

  /**
   * Saves the paper. `DRAFT` keeps a new paper (or a draft) back from the
   * class; `PUBLISHED` schedules it, or releases a draft. A published paper is
   * never taken back to a draft — the class has already been told of it.
   */
  async function save(publication: ExamPublication) {
    if (isSaving || endsBeforeItStarts || passAboveTotal) return;

    if (!isEditing && !subjectUuid) {
      toast.error('Choose a subject', 'A paper belongs to one subject of this section.');
      return;
    }

    setSavingAs(publication);
    try {
      // Field by field, and the same eight either way — the difference is only
      // that a paper's section and subject are fixed once it exists.
      const fields = {
        name: name.trim(),
        examType,
        maxMarks: Number(maxMarks),
        passMarks: passMarks.trim() === '' ? null : Number(passMarks),
        examDate,
        startTime,
        endTime,
        balancePlan,
      };

      // Only a draft being released says so; a draft saved again stays one.
      const isReleasing = isEditing && exam.publication === 'DRAFT' && publication === 'PUBLISHED';
      const saved = isEditing
        ? await examsApi.update(exam.uuid, {
            ...fields,
            ...(isReleasing && { publication: 'PUBLISHED' as const }),
          })
        : await examsApi.create({
            classSectionUuid: sectionUuid,
            subjectUuid,
            ...fields,
            publication,
          });

      const when = `${sectionName} · ${EXAM_SESSION_LABEL[session]}, ${formatClockTime(startTime)} – ${formatClockTime(endTime)}.`;
      toast.success(
        saved.publication === 'DRAFT'
          ? `${saved.name} is saved as a draft`
          : isEditing && !isReleasing
            ? `${saved.name} is updated`
            : `${saved.name} is scheduled`,
        saved.publication === 'DRAFT'
          ? `${when} Students, parents and the section's teachers see it once it is published.`
          : when,
      );
      onSaved();
      onClose();
    } catch (caught) {
      toast.error(
        isEditing ? 'Could not update that paper' : 'Could not schedule that paper',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setSavingAs(null);
    }
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    void save('PUBLISHED');
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEditing ? 'Edit examination paper' : 'Schedule an examination paper'}
      description={`${sectionName} · ${isEditing ? capitalizeWords(exam.subject.name) : 'choose the subject below'}`}
      size="lg"
      footer={
        <>
          <Button variant="secondary" size="md" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          {canSaveDraft && (
            <Button
              variant="secondary"
              size="md"
              onClick={() => void save('DRAFT')}
              isLoading={savingAs === 'DRAFT'}
              disabled={(!isEditing && !hasSubjects) || savingAs === 'PUBLISHED'}
            >
              {isEditing ? 'Save draft' : 'Save as draft'}
            </Button>
          )}
          <Button
            variant="primary"
            size="md"
            type="submit"
            form="exam-paper-form"
            isLoading={savingAs === 'PUBLISHED'}
            disabled={(!isEditing && !hasSubjects) || savingAs === 'DRAFT'}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold"
          >
            {!isEditing ? 'Schedule paper' : exam.publication === 'DRAFT' ? 'Publish' : 'Save changes'}
          </Button>
        </>
      }
    >
      <form id="exam-paper-form" onSubmit={handleSubmit} className="flex flex-col gap-5 py-1">
        {!isEditing && !hasSubjects && (
          <p className="text-sm text-slate-600 dark:text-slate-400">
            No subjects are assigned to {sectionName} yet. Assign subjects to this class first to schedule an examination paper.
          </p>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {isEditing ? (
            <Input
              label="Subject"
              value={capitalizeWords(exam.subject.name)}
              readOnly
              disabled
              hint="A paper cannot move subject — its marks belong to this one."
            />
          ) : (
            <Select
              label="Subject"
              options={subjects.map((subject) => ({
                value: subject.uuid,
                label: subject.teacher ? `${capitalizeWords(subject.name)} — ${subject.teacher}` : capitalizeWords(subject.name),
              }))}
              value={subjectUuid}
              onChange={(event) => setSubjectUuid(event.target.value)}
              disabled={!hasSubjects}
            />
          )}

          <Input
            label="Paper title"
            placeholder="e.g. Paper 1, Half-Yearly"
            required
            minLength={2}
            maxLength={120}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Select
            label="Kind of paper"
            options={instituteExamTypeOptions(exam?.examType)}
            value={examType}
            onChange={(event) => setExamType(event.target.value as ExamType)}
          />
          <DatePicker
            label="Exam date"
            required
            value={examDate}
            min={earliestExamDate(exam?.examDate)}
            dayNote={holidayDayNote(holidays)}
            dayNoteLegend={HOLIDAY_DAY_NOTE_LEGEND}
            onChange={(event) => setExamDate(event.target.value)}
          />
        </div>

        <fieldset className="flex flex-col gap-2">
          <legend className="lbl mb-1">Timings</legend>

          <div className="grid grid-cols-3 gap-3">
            {TIMING_PRESETS.map((presetOption) => {
              const Icon = presetOption.icon;
              const isActive =
                startTime === presetOption.startTime && endTime === presetOption.endTime;

              return (
                <button
                  key={presetOption.value}
                  type="button"
                  onClick={() => {
                    setStartTime(presetOption.startTime);
                    setEndTime(presetOption.endTime);
                  }}
                  className={cn(
                    'py-2.5 px-3 rounded-xl border text-sm font-bold flex items-center justify-center gap-2 transition-all cursor-pointer shadow-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                    isActive
                      ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                      : 'bg-slate-50 dark:bg-[#0b1322] text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800',
                  )}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span>{presetOption.label}</span>
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-1">
            <Input
              label="Starts"
              type="time"
              required
              value={startTime}
              onChange={(event) => setStartTime(event.target.value)}
            />
            <Input
              label="Ends"
              type="time"
              required
              value={endTime}
              onChange={(event) => setEndTime(event.target.value)}
              error={endsBeforeItStarts ? 'A paper cannot end before it starts' : undefined}
            />
          </div>

          <p className="text-xs font-bold text-slate-500 dark:text-slate-400">
            Printed as a {EXAM_SESSION_LABEL[session].toLowerCase()} —{' '}
            {formatClockTime(startTime)} to {formatClockTime(endTime)}.
          </p>
        </fieldset>

        <Select
          label="Rest of that day"
          options={EXAM_BALANCE_PLAN_OPTIONS}
          value={balancePlan}
          onChange={(event) => setBalancePlan(event.target.value as ExamBalancePlan)}
          hint="What the children do in the half of the day they are not sitting this paper."
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Max marks"
            type="number"
            min={1}
            max={1000}
            required
            value={maxMarks}
            onChange={(event) => setMaxMarks(event.target.value)}
          />
          <Input
            label="Pass marks (optional)"
            type="number"
            min={0}
            max={1000}
            placeholder="Leave empty for the institute's own pass mark"
            value={passMarks}
            onChange={(event) => setPassMarks(event.target.value)}
            error={passAboveTotal ? 'The pass mark cannot be above the total' : undefined}
          />
        </div>
      </form>
    </Modal>
  );
}
